import type { ReactNode } from "react";
import { styled } from "@pigment-css/react";
import { ChevronRight } from "@acto/web-common/components";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";

const Banner = styled("button")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  boxSizing: "border-box",
  padding: `${theme.fieldSalesTwo.spacing[20]} ${theme.fieldSalesTwo.spacing[24]}`,
  background: theme.fieldSalesTwo.colors.surface.glass,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
  borderRadius: theme.fieldSalesTwo.radius["38"],
  cursor: "pointer",
  textAlign: "left",
  font: "inherit",
  transition: "background-image 120ms ease",
  "&:hover": { backgroundImage: "linear-gradient(rgba(19, 19, 19, 0.04), rgba(19, 19, 19, 0.04))" },
  "&:active": { backgroundImage: "linear-gradient(rgba(19, 19, 19, 0.08), rgba(19, 19, 19, 0.08))" },
  ...pressInteraction(theme, { scale: 1 }),
}));

const Content = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[14],
  minWidth: 0,
}));

const TextColumn = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[2],
  minWidth: 0,
}));

const IconWrap = styled("span")(({ theme }) => ({
  display: "flex",
  color: theme.fieldSalesTwo.colors.charts.primary,
}));

const Label = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[440],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const Title = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[850],
}));

const SubLabel = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight[440],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[800],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const Chevron = styled(ChevronRight)(({ theme }) => ({
  color: theme.fieldSalesTwo.colors.neutral[800],
  width: theme.fieldSalesTwo.spacing[12],
  height: theme.fieldSalesTwo.spacing[12],
  flexShrink: 0,
}));

interface NudgeBannerProps {
  /** Leading icon; rendered inside an accent-coloured wrap, so use `currentColor` strokes. */
  icon?: ReactNode;
  label: string;
  /** Second line, truncated to a single line; when set, the label becomes the bold title above it. */
  subLabel?: string;
  onClick: () => void;
  className?: string;
}

/**
 * Home nudge pill: a full-width glass button with a leading accent icon, a label and a
 * trailing chevron. Presentational only — data/visibility/navigation live in the consumer
 * (see `RecapEntryBanner`, `OverdueTodosBanner`).
 */
export function NudgeBanner({ icon, label, subLabel, onClick, className }: NudgeBannerProps) {
  return (
    <Banner type="button" className={className} onClick={onClick}>
      <Content>
        {icon && <IconWrap>{icon}</IconWrap>}
        {subLabel ? (
          <TextColumn>
            <Title>{label}</Title>
            <SubLabel>{subLabel}</SubLabel>
          </TextColumn>
        ) : (
          <Label>{label}</Label>
        )}
      </Content>
      <Chevron />
    </Banner>
  );
}
